import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import type { Document } from "@/features/documents/types";
import { formatFileSize } from "@/features/utils/numbers";
import type { DateFormat } from "@/features/utils/dates";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { CheckCircleIcon, FileText, GlobeIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useMetadataConfig } from "@/features/documents/hooks/use-metadata-config";
import type { NumberFormat } from "@/features/documents/types";
import { getColorForString, type Tone } from "@/features/utils/colors";
import { Action } from "./columns-action";

/**
 * Values a document's metadata spells out a judgement with: a yes/no, and the
 * A–F energy rating (best to worst). Anything else a tenant put in a select
 * carries no meaning of its own and gets a stable hashed colour.
 */
const VALUE_TONES: Record<string, Tone> = {
  Yes: "green",
  No: "red",
  A: "green",
  B: "green",
  C: "yellow",
  D: "orange",
  E: "orange",
  F: "red",
};

const formatMetadataValue = (
  value: unknown,
  type: string,
  numberFormat: NumberFormat | undefined,
  dates: DateFormat,
  yesNo: [string, string],
): React.ReactNode => {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground">-</span>;
  }

  switch (type) {
    case "boolean": {
      const boolValue = Boolean(value);
      const label = boolValue ? yesNo[0] : yesNo[1];
      return <Badge color={boolValue ? "green" : "red"}>{label}</Badge>;
    }
    case "select": {
      const strValue = String(value);
      return (
        <Badge color={VALUE_TONES[strValue] || getColorForString(strValue)}>
          {strValue}
        </Badge>
      );
    }
    case "date":
      return dates.format(value as number, "P");
    case "number":
      return formatNumber(value as number, numberFormat);
    default:
      return String(value);
  }
};

const formatNumber = (value: number, format?: NumberFormat): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  switch (format) {
    case "currency":
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    case "area_m2":
      return `${value.toLocaleString("fr-FR")} m²`;
    case "volume_m3":
      return `${value.toLocaleString("fr-FR")} m³`;
    case "percentage":
      return `${value.toLocaleString("fr-FR")}%`;
    case "decimal":
      return value.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    case "number":
    default:
      return value.toLocaleString("fr-FR");
  }
};

export const useDocumentsColumns = () => {
  const { t } = useTranslation("documents");
  const dates = useDateFormat();
  const { data: metadataConfig } = useMetadataConfig();

  const columns: ColumnDef<Document>[] = [
    {
      accessorKey: "filename",
      header: t("table.document"),
      cell: ({ row }) => {
        const doc = row.original;
        return (
          <div className="flex items-center gap-3 max-w-sm w-max">
            {doc.source_url ? (
              <GlobeIcon className="h-5 w-5 text-muted-foreground shrink-0" />
            ) : (
              <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
            )}
            <div className="min-w-0">
              <div className="font-medium line-clamp-2">{doc.filename}</div>
              {doc.source_url ? (
                <a
                  href={doc.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="block max-w-xs truncate text-xs text-muted-foreground hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {doc.source_url.replace(/^https?:\/\//, "")}
                </a>
              ) : (
                <div className="text-xs text-muted-foreground">
                  {formatFileSize(doc.file_size)} • {doc.mime_type}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
  ];

  // Add dynamic metadata columns
  if (metadataConfig?.fields?.fields) {
    metadataConfig.fields.fields.forEach((field) => {
      columns.push({
        id: `metadata_${field.id}`,
        accessorKey: `metadata.${field.id}`,
        header: field.label,
        cell: ({ row }) => {
          const value = row.original.metadata?.[field.id];
          return formatMetadataValue(
            value,
            field.type,
            field.numberFormat,
            dates,
            [t("common.yes"), t("common.no")],
          );
        },
      });
    });
  }

  columns.push(
    ...[
      {
        id: "ready",
        header: t("table.ready"),
        cell: ({ row }) => {
          const doc = row.original;
          const isReady =
            doc.status === "completed" &&
            doc.embedding_status === "completed" &&
            doc.chunk_count > 0;

          return (
            <Badge
              icon={isReady ? CheckCircleIcon : Spinner}
              variant={isReady ? "default" : "outline"}
              className="gap-1"
            >
              {isReady ? <>{t("status.ready")}</> : <>{t("status.pending")}</>}
            </Badge>
          );
        },
      } as ColumnDef<Document>,
      {
        accessorKey: "created_at",
        header: t("table.uploaded"),
        cell: ({ row }) => {
          return (
            <div className="text-sm text-muted-foreground">
              {dates.format(row.original.created_at, "Pp")}
            </div>
          );
        },
      } as ColumnDef<Document>,
    ],
  );

  // Add actions column at the end
  columns.push({
    id: "actions",
    header: () => <div className="text-right">{t("table.actions")}</div>,
    cell: ({ row }) => <Action document={row.original} />,
  });

  return columns;
};
